import { useEffect, useMemo, useState } from 'react';
import { EyeOff } from 'lucide-react';
import { db } from '@/lib/db';
import { notifyFollowingChanged } from '@/lib/following-events';
import type { MemberPublic, MemberWithStats, PostEnriched } from '@/lib/db/types';
import { Sheet } from '@/components/shared/Sheet';
import { Avatar } from '@/components/shared/Avatar';
import { GhinDetailModal } from '@/components/profile/GhinDetailModal';
import { ProfileHeroStats } from '@/components/profile/ProfileHeroStats';
import { RoundSnapshotSheet } from '@/components/profile/RoundSnapshotSheet';
import { personalizeRoundPostForMember } from '@/lib/profile/member-round-history';
import { holesLabel, playedPar, scoreToParLabel } from '@/lib/scoring/played-par';
import { clubIndex } from '@/lib/board/club-index';

interface Props {
  memberId: string;
  viewerId: string;
  onClose: () => void;
}

export function ProfileModal({ memberId, viewerId, onClose }: Props) {
  const [profile, setProfile] = useState<MemberWithStats | null>(null);
  const [posts, setPosts] = useState<PostEnriched[]>([]);
  const [following, setFollowing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showGhin, setShowGhin] = useState(false);
  const [selectedRoundPost, setSelectedRoundPost] = useState<PostEnriched | null>(null);
  const [hidingPostId, setHidingPostId] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    setProfile(null);
    setPosts([]);
    void (async () => {
      try {
        const [p, roundHistory, isF] = await Promise.all([
          db.members.getWithStats(memberId),
          db.posts.listMemberRoundHistory(memberId, { limit: 12, includeCourseHoles: true }),
          db.follows.isFollowing(memberId),
        ]);
        if (!active) return;
        if (!p) {
          setError('Could not load this profile.');
          return;
        }
        setProfile(p);
        setPosts(roundHistory.map((post) => personalizeRoundPostForMember(post, memberId)));
        setFollowing(isF);
      } catch (err) {
        if (active) setError((err as Error).message || 'Could not load profile');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [memberId]);

  const roundPosts = useMemo(
    () => posts.filter((p) => p.type === 'round' && p.round?.gross_total != null),
    [posts],
  );

  const anccIndex = useMemo(() => {
    const rounds = roundPosts
      .filter((p) => p.round?.holes === 'full' && p.round?.tee?.slope != null && p.round?.tee?.course_rating != null)
      .map((p) => ({
        member_id: memberId,
        gross_total: p.round!.gross_total!,
        holes: p.round!.holes,
        slope: p.round!.tee!.slope,
        course_rating: p.round!.tee!.course_rating,
        played_at: p.round!.played_at,
      }));
    const idx = clubIndex(rounds, memberId);
    return idx != null ? idx.toFixed(1) : '—';
  }, [roundPosts, memberId]);

  const toggleFollow = async () => {
    if (following) {
      await db.follows.remove(memberId);
      setFollowing(false);
      notifyFollowingChanged(memberId, false);
    } else {
      await db.follows.add(memberId);
      setFollowing(true);
      notifyFollowingChanged(memberId, true);
    }
  };

  const hideRoundFromHistory = async (postId: string) => {
    setHidingPostId(postId);
    const prev = posts;
    setPosts((cur) => cur.filter((p) => p.id !== postId));
    try {
      await db.posts.hideRoundFromMyHistory(postId);
    } catch (err) {
      setPosts(prev);
      setError((err as Error).message || 'Could not hide round');
    } finally {
      setHidingPostId(null);
    }
  };

  if (loading) {
    return <Sheet title="Profile" onClose={onClose}><div className="p-4 text-sm text-mute">Loading…</div></Sheet>;
  }

  if (error || !profile) {
    return (
      <Sheet title="Profile" onClose={onClose}>
        <div className="space-y-3 p-4">
          <div className="text-sm text-red-600">{error || 'Profile unavailable.'}</div>
          <button type="button" onClick={onClose} className="w-full rounded-full border border-line py-2.5 text-sm font-semibold text-navy">Close</button>
        </div>
      </Sheet>
    );
  }

  if (showGhin) {
    return <GhinDetailModal profile={profile} onClose={() => setShowGhin(false)} />;
  }
  if (selectedRoundPost) {
    return (
      <RoundSnapshotSheet
        post={selectedRoundPost}
        viewerMemberId={memberId}
        onClose={() => setSelectedRoundPost(null)}
      />
    );
  }

  return (
    <Sheet title={profile.name} onClose={onClose}>
      <div className="space-y-4 p-4 pb-8">
        <div className="overflow-hidden rounded-2xl bg-navy text-cream shadow-md">
          <div className="flex items-start gap-4 p-4">
            <Avatar member={profile as MemberPublic} size={68} />
            <div className="min-w-0 flex-1">
              <div className="font-serif text-2xl font-bold tracking-tight">{profile.name}</div>
              <div className="mt-0.5 text-sm text-white/60">@{profile.handle}</div>
              {profile.about_me && <p className="mt-2 text-sm leading-relaxed text-white/75">{profile.about_me}</p>}
            </div>
          </div>
          <ProfileHeroStats
            stats={[
              {
                label: 'Index',
                value: profile.handicap != null ? profile.handicap.toFixed(1) : '—',
                onClick: () => setShowGhin(true),
              },
              { label: 'ANCC', value: anccIndex },
              { label: 'Rounds', value: profile.rounds_count || roundPosts.length },
            ]}
          />
        </div>
        <button type="button" onClick={() => void toggleFollow()} className={`w-full rounded-full py-2.5 text-sm font-bold ${following ? 'border border-line bg-cream text-ink-soft' : 'bg-navy text-cream'}`}>
          {following ? 'Following' : 'Follow'}
        </button>
        <div>
          <div className="mb-2 text-xs font-bold uppercase tracking-wider text-mute">Recent rounds</div>
          {roundPosts.length === 0 && <div className="text-sm text-mute">No rounds yet.</div>}
          {roundPosts.slice(0, 8).map((p) => {
            const gross = p.round!.gross_total!;
            const holeRange = p.round!.holes ?? 'full';
            const par = p.round?.course
              ? playedPar(holeRange, p.round.course.par, p.round.course_holes)
              : (holeRange === 'full' ? 72 : 36);
            const course = p.round?.course?.name || p.metadata?.courseName || 'Course';
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => setSelectedRoundPost(p)}
                className="mb-2 flex w-full items-center gap-3 rounded-[14px] border border-line bg-cream-warm p-3 text-left shadow-bt"
              >
                <div className="flex h-12 w-12 shrink-0 flex-col items-center justify-center rounded-lg bg-navy text-cream">
                  <span className="font-serif text-base font-bold leading-none">{gross}</span>
                  <span className="font-mono text-[9px] text-gold">{scoreToParLabel(gross, par)}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-serif text-sm font-semibold text-ink">{course}</div>
                  <div className="mt-0.5 text-[11px] text-mute">
                    {new Date(p.round?.played_at || p.created_at).toLocaleString([], {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                    {p.round?.holes ? ` · ${holesLabel(p.round.holes)}` : ''}
                  </div>
                </div>
                {viewerId === memberId && (
                  <button
                    type="button"
                    disabled={hidingPostId === p.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      void hideRoundFromHistory(p.id);
                    }}
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] border border-line bg-paper text-mute disabled:opacity-50"
                    aria-label="Hide round from my history"
                  >
                    <EyeOff size={15} />
                  </button>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </Sheet>
  );
}
